import { getSingleInstanceByElement } from "../../../core";
import Component from "../../component.abstract";
import SuggestInput from "../../inputs/autocomplete/autocomplete";
import Selector from "../../multiselect/select";

/**
 * Allow to validate an input field
 * 
 * To parameter, add these attributes to the element:
 * - data-val-regex-pattern="[a-z]*" to add a regular expression
 * - data-val-regex="MESSAGE" to add a custom message for the previous validation
 * - data-val-required or data-rule-required to make it mandatory (no value needed)
 * - data-msg-required="MESSAGE" to add a custom message for the previous validation
 * - data-rule-minlength="1" to add a minimum number of characters
 * - data-msg-minlength="MESSAGE" to add a custom message for the previous validation
 * - data-rule-maxlength="10" to add a maximum number of characters
 * - data-msg-maxlength="MESSAGE" to add a custom message for the previous validation
 * - data-rule-minnumber="1" to add a minimal value
 * - data-msg-minnumber="MESSAGE" to add a custom message for the previous validation
 * - data-rule-maxnumber="10" to add a maximal value
 * - data-msg-maxnumber="MESSAGE" to add a custom message for the previous validation
 * - data-rule-step to set a value interval
 * - data-msg-step="MESSAGE" to add a custom message for the previous validation
 * - data-val-test-every-key="false" to avoid testing every time a user press a key (default is "true")
 * 
 * The error messages will be displayed in the elements (div, span, etc.) that have 
 * data-valmsg-for attribute with same value as the current element name 
 */
export default class ValidatedInput extends Component {
    private id: string;
    private name: string;
    private regex: string;
    private errorMessage = "Erreur de saisie";
    private required: boolean;
    private requiredMessage = "Veuillez remplir ce champ.";
    private minLength: number;
    private maxLength: number;
    private minLengthMessage: string;
    private maxLengthMessage: string;
    private trimMaxLength = true;
    private minNumber: number;
    private maxNumber: number;
    private minNumberMessage: string;
    private maxNumberMessage: string;
    private step: number;
    private stepMessage: string;
    private messages: Array<any>;
    private valid: boolean;
    private multipleValues = false;
    private testOnEveryKeyUp = "true";
    private isAutocomplete = false;
    private isMultiselect = false;
    private multiselectInstance: Selector;

    constructor (
        props: any,
        element: HTMLElement
    ) {  
        super(props, element);

        this.id = this.element.getAttribute("id");
        this.name = this.element.getAttribute("name").replace("-notused", "");
        this.valid = false;

        // Get validation parameters
        this.regex = this.element.getAttribute("data-val-regex-pattern");
        if (this.regex) {
            const regexMessage: string = this.element.getAttribute("data-val-regex");
            if (regexMessage) {
                this.errorMessage = regexMessage;
            }
        }
        if(this.element.getAttribute("data-val-test-every-key")) {
            this.testOnEveryKeyUp = this.element.getAttribute("data-val-test-every-key")
        }

        this.required = this.element.hasAttribute("data-val-required") ||
            this.element.hasAttribute("data-rule-required");
        if ( this.required) {
            const message: string = this.element.getAttribute("data-msg-required");
            if (message) {
                this.requiredMessage = message;
            }
        }

        const minLengthString: string = this.element.getAttribute("data-rule-minlength");
        if (minLengthString) {
            try {
                this.minLength = parseInt(minLengthString);
            } catch(e) { e }
        }

        const maxLengthString: string = this.element.getAttribute("data-rule-maxlength");
        if (maxLengthString) {
            try {
                this.maxLength = parseInt(maxLengthString);
            } catch(e) { e }
        }

        if (!isNaN(this.minLength) &&
            !isNaN(this.maxLength) &&
            this.minLength > this.maxLength) {
            // Swap values
            [this.minLength, this.maxLength] = [this.maxLength, this.minLength];
        }

        if (!isNaN(this.minLength)) {
            const minLengthMessage: string = this.element.getAttribute("data-msg-minlength");
            if (minLengthMessage) {
                this.minLengthMessage = minLengthMessage;
            } else {
                this.minLengthMessage = this.minLength + " caractères minimum";
            }
        }

        if (!isNaN(this.maxLength)) {
            const maxLengthMessage: string = this.element.getAttribute("data-msg-maxlength");
            if (maxLengthMessage) {
                this.maxLengthMessage = maxLengthMessage;
            } else {
                this.maxLengthMessage = this.maxLength + " caractères maximum";
            }
        }

        const minNumberString: string = this.element.getAttribute("data-rule-minnumber");
        if (minNumberString) {
            try {
                this.minNumber = parseInt(minNumberString);
            } catch(e) { e }
        }

        const maxNumberString: string = this.element.getAttribute("data-rule-maxnumber");
        if (maxNumberString) {
            try {
                this.maxNumber = parseInt(maxNumberString);
            } catch(e) { e }
        }

        if (!isNaN(this.minNumber) &&
            !isNaN(this.maxNumber) &&
            this.minNumber > this.maxNumber) {
            // Swap values
            [this.minNumber, this.maxNumber] = [this.maxNumber, this.minNumber];
        }

        if (!isNaN(this.minNumber)) {
            const minNumberMessage: string = this.element.getAttribute("data-msg-minnumber");
            if (minNumberMessage) {
                this.minNumberMessage = minNumberMessage;
            } else {
                this.minNumberMessage = this.minNumber + " minimum";
            }
        }

        if (!isNaN(this.maxNumber)) {
            const maxNumberMessage: string = this.element.getAttribute("data-msg-maxnumber");
            if (maxNumberMessage) {
                this.maxNumberMessage = maxNumberMessage;
            } else {
                this.maxNumberMessage = this.maxNumber + " maximum";
            }
        }

        const stepString = this.element.getAttribute("data-rule-step");
        if (stepString) {
            try {
                this.step = parseInt(stepString);
            } catch(e) { e }
        }

        if(!isNaN(this.step)) {
            const stepMessage: string = this.element.getAttribute("data-msg-step");
            if (stepMessage) {
                this.stepMessage = stepMessage;
            } else {
                this.stepMessage =  "Votre réponse ne respecte pas l'intervalle de " + this.step
            }
        }

        if (this.name) {
            this.messages = [];
            // Get message elements related to this input
            document.querySelectorAll("[data-valmsg-for]").forEach(message => {
                // Data can be a string or an object with a name property
                let data: any;
                const rawData: any = message.getAttribute("data-valmsg-for");
                try {
                    data = JSON.parse(rawData);
                } catch (error) {
                    data = {
                        "name": rawData
                    };
                }

                if (data.name && data.name === this.name) {
                    this.messages.push({
                        "element" : message, 
                        "error" : (data.error ? data.error : this.errorMessage), 
                        "valid" : (data.valid ? data.valid : message.innerHTML)
                    });
                }
            });
        }

        // Handle case of multiple values for autocomplete inputs
        if (this.element.hasAttribute("data-autocomplete")) {
            try {
                this.isAutocomplete = true;
                const autocompleteConfiguration = JSON.parse(this.element.getAttribute("data-autocomplete"));
                this.multipleValues = autocompleteConfiguration.displayBelow === true || 
                    autocompleteConfiguration.displayBelow === "true";
                if (autocompleteConfiguration.displayMessage) {
                    this.requiredMessage = autocompleteConfiguration.displayMessage;
                }
                
            } catch (error) { error }
        } else if (this.element.tagName.toLowerCase() === "select") {
            this.isMultiselect = true;
            this.multiselectInstance = getSingleInstanceByElement("Selector", this.element);
        }

        if (this.element.hasAttribute("data-trim-maxLength")) {
            this.trimMaxLength = this.element.getAttribute("data-trim-maxLength") !== "false";
        }
    }

    mounted(): void {
        // Check validity on user actions
        this.element.addEventListener("change", () => {
            this.check();
        });

        if(this.testOnEveryKeyUp === "true") {
            this.element.addEventListener("input", () => {
                this.check();
            });
        }

        this.element.addEventListener("keydown", (e: KeyboardEvent) => {
            if ((this.element.nodeName != "TEXTAREA") && (e.key === "Enter" || e.key === "Tab")) {
                this.check();

                if (this.valid) {
                    // Leave the element
                    this.element.blur();
                }
            }
        });

        // If there is already a value, check it
        if ((<HTMLInputElement> this.element).value.length > 0) {
            this.check();
        }

        PubSub.subscribe("account-data-ready", () => {
             if ((<HTMLInputElement> this.element).value.length > 0) {
                this.check();
             }
        });
    
        PubSub.subscribe("suggestInput.addTag", (msg: string, autocomplete: SuggestInput) => {
            if (autocomplete.name === this.name) {
                this.check();
            }
        });

        PubSub.subscribe("suggestInput.removeTag", (msg: string, autocomplete: SuggestInput) => {
            if (autocomplete.name === this.name) {
                this.check();
            }
        });

        PubSub.subscribe("suggestInput.blur", (msg: string, autocomplete: SuggestInput) => {
            if (autocomplete.name === this.name) {
                this.check();
            }
        });
    }

    getId(): string {
        return this.id;
    }

    getValue(): string {
        return this.valid ? (<HTMLInputElement> this.element).value : "";
    }

    isValid(): boolean {
        return this.valid;
    }

    /**
     * Used by international input to change country regex
     */
    refreshPattern() {
        const regex = this.element.getAttribute("data-val-regex-pattern");
        if (regex && regex != "") {
            this.regex = regex;
        }
    }

    /**
     * Check input validity per parameters
     */
    check(): boolean {
        this.valid = true;
        let errorMessage: string, 
            rawValue: string,
            value: string;

        if (this.isAutocomplete) {
            if (this.multipleValues) {
            // Get value from autocomplete tags
            const items: NodeListOf<HTMLInputElement> = document.querySelectorAll('[data-autocomplete-tags="' + this.name + 
                '"] input[name="' + this.name + '"]');
            value = Array.from(items).map(item => item.value).join(",");
            } else {
                value = (<HTMLInputElement>document.querySelector('input[data-autocomplete-input-name="' + this.name + '"]')).value
            }
        } else if (this.isMultiselect) {
            if (this.multiselectInstance) {
                value = this.multiselectInstance.getValue().join();
            } else {
                value = "";
            }
        } else {
            rawValue = (<HTMLInputElement> this.element).value;
            value = rawValue.trim();
        }

        if (this.required || value.length > 0) {
            if (this.required && value.length === 0) {
                this.valid = false;
                errorMessage = this.requiredMessage;
            }

            if (this.valid && this.minLength > 0 && value.length < this.minLength) {
                this.valid = false;
                errorMessage = this.minLengthMessage;
            }

            if (this.valid && this.maxLength > 0) {
                const valueToCheck = this.trimMaxLength ? value : rawValue;
                if (valueToCheck.length > this.maxLength) {
                    this.valid = false;
                    errorMessage = this.maxLengthMessage;
                }
            }

            if (this.valid && this.minNumber >= 0 && parseInt(value) < this.minNumber) {
                this.valid = false;
                errorMessage = this.minNumberMessage;
            }

            if (this.valid && this.maxNumber > 0 && parseInt(value) > this.maxNumber) {
                this.valid = false;
                errorMessage = this.maxNumberMessage;
            }

            if (this.valid && this.step > 0 && (parseInt(value) % this.step) !== 0) {
                this.valid = false;
                errorMessage = this.stepMessage;
            }

            if (this.valid && this.regex) {
                const reg = new RegExp(this.regex);
                this.valid = reg.test(rawValue);
            }
        }

        // Update the UI
        if (this.valid) {
            // Hide the error messages
            this.clearErrors();
        } else {
            // Show the error messages
            this.messages.forEach(message => {
                message.element.classList.add("error");
                message.element.classList.remove("valid");
                message.element.innerHTML = errorMessage ? errorMessage : message.error;
            });
            this.element.classList.remove("valid");
            this.element.classList.add("error");
        }

        return this.valid; 
    }

    clearErrors(): void {
        this.messages.forEach(message => {
            message.element.classList.remove("error");
            message.element.classList.add("valid");
            message.element.innerHTML = message.valid;
        });
        this.element.classList.remove("error");
        this.element.classList.add("valid");
    }
}